import React from "react";
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';

const ErrorCodes = () => {
    const response_401 = `{
  "statusCode": 401,
  "message": "You are not authorized."
}`;

    const response_others = `{
  "errorCode": "WP12",
  "message": "No terminal found in Unlinked state for given registration code : 234589",
  "timestamp": "2023-07-04T14:03:59.933704Z"
}`;

const validationErrorFormat = `{
    "type": "https://tools.ietf.org/html/rfc7231#section-6.5.1",
    "title": "One or more validation errors occurred.",
    "status": 400,
    "traceId": "00-987ce5c7034493344e86794d873995e8-ee4c3502d661010a-00",
    "errors": {
      "Amount": [
        "Invalid amount format. Expected format: ##.## or field not present"
      ],
      "TerminalId": [
        "The TerminalId field is required."
      ],
      "CurrencyCode": [
        "Invalid currency code or field not present."
      ]
    }
  }`


    return (
        <article className="docs-article" id="section-8">
            <header className="docs-header">
                <h1 className="docs-heading">Error Handling<span className="docs-time">Last updated: 2023-07-05</span>
                </h1>
                <section className="docs-intro">
                    <p>Westpay AB has implemented a set of error codes that serve as valuable tools for effective error handling on the client side. These error codes provide specific and standardized identifiers for different types of errors, allowing developers to easily recognize and respond to them. By leveraging these error codes, developers can swiftly diagnose and resolve issues, leading to improved software stability and a more seamless user experience.</p>
                </section>

                <section className="docs-section" id="error-codes">
                    <h5>Error codes</h5>
                    <div className="table-responsive my-4">
                        <table className="table table-bordered">
                            <thead>
                                <tr>
                                    <th className="heading theme-bg-light"><a className="theme-nolink" >Error Code</a></th>
                                    <th className="heading theme-bg-light"><a className="theme-nolink" >Description</a></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP01">WP01</a></th>
                                    <td>Terminal not linked to a terminal ID</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP02">WP02</a></th>
                                    <td>Terminal not found</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP03">WP03</a></th>
                                    <td>Merchant inactive</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP04">WP04</a></th>
                                    <td>Transaction not found</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP05">WP05</a></th>
                                    <td>Unique constraint exception</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP06">WP06</a></th>
                                    <td>Reference constraint exception</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP07">WP07</a></th>
                                    <td>Cannot insert null exception</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP08">WP08</a></th>
                                    <td>Internal server error</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP09">WP09</a></th>
                                    <td>Terminal is busy processing another transaction</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP10">WP10</a></th>
                                    <td>Unable to generate a unique registration code. Try later! serialNo: </td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP11">WP11</a></th>
                                    <td>Given serial number is already linked to a terminal ID</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP12">WP12</a></th>
                                    <td>No terminal found in Unlinked state for given registration code</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP13">WP13</a></th>
                                    <td>No record found for Terminal Id</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP14">WP14</a></th>
                                    <td>No record found for Merchant Id</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP15">WP15</a></th>
                                    <td>Merchant not active</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP16">WP16</a></th>
                                    <td>Specified terminal does not belong to the merchant</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP17">WP17</a></th>
                                    <td>None(Unknown)</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP18">WP18</a></th>
                                    <td>No transaction is processing currently in the specified terminal</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP19">WP19</a></th>
                                    <td>Error while generating JWT Token</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP20">WP20</a></th>
                                    <td>MerchantID cannot be null or empty</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP21">WP21</a></th>
                                    <td>TerminalID cannot be null or empty</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP22">WP22</a></th>
                                    <td>PaymentMethod recieved from ECR is invalid</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP23">WP23</a></th>
                                    <td>No SignalR connection found for the terminal</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP24">WP24</a></th>
                                    <td>Failed sending SignalR notification to terminal</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP25">WP25</a></th>
                                    <td>Acknowledgement timed-out from terminal</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP26">WP26</a></th>
                                    <td>Only 30 days of data is allowed to retrieve</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP27">WP27</a></th>
                                    <td>Only past 90 days data is availble to retrieve</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP28">WP28</a></th>
                                    <td>Database concurrency issue</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP29">WP29</a></th>
                                    <td>One or more required fields missing</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP30">WP30</a></th>
                                    <td>Terminal has acknowledged the previous transaction, but it has not send a response. Hence user may cancel the previous transaction if its timed-out</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP31">WP31</a></th>
                                    <td>Transaction is not in a valid state</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP32">WP32</a></th>
                                    <td>Token validity period should be greater than 0</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP33">WP33</a></th>
                                    <td>Terminal is already linked to a serial number</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP34">WP34</a></th>
                                    <td>No print request found with Id</td>
                                </tr>
                                <tr>
                                    <th className="theme-bg-light"><a className="theme-nolink" href="#WP35">WP35</a></th>
                                    <td>No print support available for the terminal</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <h5 className="">401 (Unauthorized) Error Response</h5>
                    <p></p>
                    <div className="row">
                        <div className="col-md-11 col-sm-12 col-xs-12">
                            <div className="docs-code-block">
                                <pre className="shadow-lg rounded"><code className="language-dockerfile hljs">
                                    <div className="row code-header">
                                        <div className="col-md-10 languages">
                                            <span>Response</span>
                                        </div>
                                    </div>
                                    <div className="code-section">
                                        <SyntaxHighlighter language="json" style={dracula} showLineNumbers>
                                            {response_401}
                                        </SyntaxHighlighter>
                                    </div>
                                </code></pre>
                            </div>
                        </div>
                    </div>

                    <h5 className="">400 (Validation Error) Response Format</h5>
                    <p></p>
                    <div className="row">
                        <div className="col-md-11 col-sm-12 col-xs-12">
                            <div className="docs-code-block">
                                <pre className="shadow-lg rounded"><code className="language-dockerfile hljs">
                                    <div className="row code-header">
                                        <div className="col-md-10 languages">
                                            <span>Response</span>
                                        </div>
                                    </div>
                                    <div className="code-section">
                                        <SyntaxHighlighter language="json" style={dracula} showLineNumbers>
                                            {validationErrorFormat}
                                        </SyntaxHighlighter>
                                    </div>
                                </code></pre>
                            </div>
                        </div>
                    </div>
                    <h5 className="">Error Response for all other status codes</h5>
                    <p></p>
                    <div className="row">
                        <div className="col-md-11 col-sm-12 col-xs-12">
                            <div className="docs-code-block">
                                <pre className="shadow-lg rounded"><code className="language-dockerfile hljs">
                                    <div className="row code-header">
                                        <div className="col-md-10 languages">
                                            <span>Response</span>
                                        </div>
                                    </div>
                                    <div className="code-section">
                                        <SyntaxHighlighter language="json" style={dracula} showLineNumbers>
                                            {response_others}
                                        </SyntaxHighlighter>
                                    </div>
                                </code></pre>
                            </div>
                        </div>
                    </div>
                </section>
            </header>
        </article>
    )
}

export default ErrorCodes;